import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutGrid, List, RefreshCw, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import OptimizationDetailModal from '../../components/modals/OptimizationDetailModal';
import OptimizationDetailModalV3 from '../../components/modals/OptimizationDetailModalV3';
import NoAdsConnectedSection from 'components/common/NoAdsConnectedSection';
import NoObjectivesLockedSection from 'components/common/NoObjectivesLockedSection';
import { useAuth } from 'components/context/AuthContext';
import { useAdsModal } from 'components/context/ModalsContext';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useMetaAds } from 'hooks/useMetaAds';
import { useResponsive } from 'hooks/useResponsive';
import optimizationsApi from '../../api/optimizations';
import { getOptimizationsV2 } from '../../services/optimizations';
import { useObjetivosQuery } from '../../queries/objetivos';
import {
  suggestionToCard,
  normalizeSuggestionsResponse,
  groupsToSuggestions,
  buildKanbanGroupCardsFromFilteredGroups,
  distributeKanbanGroupCardsToBuckets,
  flattenKanbanCardsForModalNavigation,
  mergeApiGroupsByLogicalDedupeKey,
  mergeSpecialOptimizationGroups,
  KANBAN_COLUMNS,
  normalizeKanbanEstadoFilterParam,
  isGoogleAdsPlatformUi,
} from '../../utils/optimizationTransformers';
import OptimizationsCountdownBanner from '../../components/OptimizationsCountdownBanner';
import OptimizacionesFilterBar from '../../components/OptimizacionesFilterBar';
import KanbanBoard from '../../components/optimizations/KanbanBoard';
import { useOptimizationGenerationToast } from '../../components/context/OptimizationGenerationToastContext';
import {
  getMustWaitForNextUpdate,
  setMustWaitForNextUpdate,
  clearMustWait,
  setGenerationAttempted,
  hasGenerationAttempted,
  clearGenerationAttempted,
} from '../../utils/optimizationGenerateStorage';
import { GoogleIcon, MetaIcon } from 'components';

const VIEW_MODE_STORAGE_KEY = 'optimizaciones.viewMode';

const Optimizaciones = () => {
  const [selectedOptimization, setSelectedOptimization] = useState(null);
  const [currentOptimizationIndex, setCurrentOptimizationIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  /** When set, detail modal navigates only within this list (e.g. one group's suggestions) */
  const [detailModalOptimizations, setDetailModalOptimizations] = useState(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeGroups, setActiveGroups] = useState([]);
  const [expiredGroups, setExpiredGroups] = useState([]);

  const navigate = useNavigate();
  const { auth } = useAuth();
  const isAdmin = Boolean(auth?.user?.is_admin);
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  const { runGeneration, isGenerating, state: generationState } = useOptimizationGenerationToast();
  const isGoogleConnected = auth?.user?.google_ads_connected && auth?.user?.google_ads_customer_id;
  const isMetaConnected = auth?.user?.meta_connected && auth?.user?.meta_ad_account_id;
  const hasAnyConnection = isGoogleConnected || isMetaConnected;
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const { openCreateObjectivesSetupModal, setIsOptimizationDetailModalOpen: setGlobalOptimizationDetailModalOpen } = useAdsModal();
  const activeBrandId = auth?.user?.active_brand_id;
  const { data: objectives = [], isPending: loadingObjectives } = useObjetivosQuery(
    activeBrandId != null ? String(activeBrandId) : null
  );

  const [optimizationsRefreshToken, setOptimizationsRefreshToken] = useState(0);
  const [mustWaitForNextUpdate, setMustWaitForNextUpdateState] = useState(false);
  const [hasTriedGeneration, setHasTriedGeneration] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroPlataforma = (searchParams.get('plataforma') || 'todas').toLowerCase();
  const filtroEstatus = normalizeKanbanEstadoFilterParam(searchParams.get('estado'));

  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) : null;
      if (v === 'list' || v === 'board') return v;
    } catch {
      /* ignore */
    }
    return 'board';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const setFiltroPlataforma = useCallback(
    (value) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('plataforma', value);
        return p;
      });
    },
    [setSearchParams]
  );

  const setFiltroEstatus = useCallback(
    (value) => {
      const normalized = normalizeKanbanEstadoFilterParam(value);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (normalized === 'todas') {
          p.delete('estado');
        } else {
          p.set('estado', normalized);
        }
        return p;
      });
    },
    [setSearchParams]
  );

  const visibleKanbanColumnKeys = useMemo(
    () =>
      filtroEstatus === 'todas' ? KANBAN_COLUMNS.map((c) => c.key) : [filtroEstatus],
    [filtroEstatus]
  );

  // Plataformas conectadas pero sin objetivos: para CTA "Creá objetivos de [plataforma] para desbloquear..."
  const platformsWithoutObjectives = useMemo(() => {
    const list = [];
    const hasGoogleObjectives = (objectives || []).some(
      (o) => (o.plataformas_relacionadas || []).includes('GOOGLE')
    );
    const hasMetaObjectives = (objectives || []).some(
      (o) => (o.plataformas_relacionadas || []).includes('META')
    );
    if (isGoogleConnected && !hasGoogleObjectives) list.push({ value: 'GOOGLE', label: 'Google Ads' });
    if (isMetaConnected && !hasMetaObjectives) list.push({ value: 'META', label: 'Meta Ads' });
    return list;
  }, [objectives, isGoogleConnected, isMetaConnected]);

  const getObjectiveName = useCallback(
    (id) => (objectives || []).find((o) => o.id === id)?.nombre ?? 'Objetivo',
    [objectives]
  );

  // Plataformas sin optimizaciones: motivo = no conectada o sin objetivos. Para píldoras turquesas debajo de la tabla.
  // Si ya tiene objetivos de esa plataforma, no mostrar "Creá objetivos" (solo conectar o nada).
  const platformsWithNoOptimizations = useMemo(() => {
    const allSuggestions = [
      ...groupsToSuggestions(activeGroups),
      ...groupsToSuggestions(expiredGroups),
    ];
    const hasOptimizationsGoogle = allSuggestions.some(
      (s) => (s.platform || '').toLowerCase().includes('google')
    );
    const hasOptimizationsMeta = allSuggestions.some(
      (s) => (s.platform || '').toLowerCase().includes('meta')
    );
    const hasGoogleObjectives = (objectives || []).some(
      (o) => (o.plataformas_relacionadas || []).includes('GOOGLE')
    );
    const hasMetaObjectives = (objectives || []).some(
      (o) => (o.plataformas_relacionadas || []).includes('META')
    );
    const list = [];
    if (!hasOptimizationsGoogle) {
      const reason = !isGoogleConnected ? 'connect' : hasGoogleObjectives ? null : 'objectives';
      if (reason) {
        list.push({
          value: 'GOOGLE',
          label: 'Google Ads',
          reason,
        });
      }
    }
    if (!hasOptimizationsMeta) {
      const reason = !isMetaConnected ? 'connect' : hasMetaObjectives ? null : 'objectives';
      if (reason) {
        list.push({
          value: 'META',
          label: 'Meta Ads',
          reason,
        });
      }
    }
    return list;
  }, [activeGroups, expiredGroups, isGoogleConnected, isMetaConnected, objectives]);

  useEffect(() => {
    setMustWaitForNextUpdateState(getMustWaitForNextUpdate(activeBrandId));
    setHasTriedGeneration(hasGenerationAttempted(activeBrandId));
  }, [activeBrandId]);

  useEffect(() => {
    const hasAny =
      groupsToSuggestions(activeGroups).length > 0 ||
      groupsToSuggestions(expiredGroups).length > 0;
    if (hasAny) {
      clearMustWait(activeBrandId);
      clearGenerationAttempted(activeBrandId);
      setMustWaitForNextUpdateState(false);
      setHasTriedGeneration(false);
    }
  }, [activeGroups, expiredGroups, activeBrandId]);

  // Admin users consume the v2 endpoint wrapper which returns the unwrapped
  // payload directly; everyone else keeps the original axios-response shape.
  const fetchOptimizationsData = useCallback(async () => {
    if (isAdmin) {
      return getOptimizationsV2();
    }
    const response = await optimizationsApi.getOptimizations();
    return response?.data;
  }, [isAdmin]);

  useEffect(() => {
    const onJobFinished = async (e) => {
      const { brandId, count } = e.detail || {};
      if (brandId !== activeBrandId) return;
      try {
        const data = await fetchOptimizationsData();
        const { activeGroups: ag, expiredGroups: eg } = normalizeSuggestionsResponse(data);
        setActiveGroups(ag);
        setExpiredGroups(eg);
      } catch (err) {
        console.error('Error refrescando optimizaciones:', err);
      }
      if (count === 0) {
        setMustWaitForNextUpdate(activeBrandId);
        setMustWaitForNextUpdateState(true);
      } else {
        clearMustWait(activeBrandId);
      }
      setOptimizationsRefreshToken((t) => t + 1);
    };
    window.addEventListener('optimization-suggestions-job-finished', onJobFinished);
    return () => window.removeEventListener('optimization-suggestions-job-finished', onJobFinished);
  }, [activeBrandId, fetchOptimizationsData]);

  useEffect(() => {
    const onIncremental = async (e) => {
      const { brandId } = e.detail || {};
      if (brandId !== activeBrandId) return;
      try {
        const data = await fetchOptimizationsData();
        const { activeGroups: ag, expiredGroups: eg } = normalizeSuggestionsResponse(data);
        setActiveGroups(ag);
        setExpiredGroups(eg);
        const hasAny =
          groupsToSuggestions(ag).length > 0 || groupsToSuggestions(eg).length > 0;
        if (hasAny) {
          clearMustWait(activeBrandId);
          setMustWaitForNextUpdateState(false);
        }
        setOptimizationsRefreshToken((t) => t + 1);
      } catch (err) {
        console.error('Error refrescando optimizaciones (incremental):', err);
      }
    };
    window.addEventListener('optimization-suggestions-incremental-refresh', onIncremental);
    return () =>
      window.removeEventListener('optimization-suggestions-incremental-refresh', onIncremental);
  }, [activeBrandId, fetchOptimizationsData]);

  const handleTestGenerateSuggestions = useCallback(async () => {
    if (!activeBrandId) {
      toast.error('No hay marca activa seleccionada');
      return;
    }
    setGenerationAttempted(activeBrandId);
    setHasTriedGeneration(true);
    clearMustWait(activeBrandId);
    setMustWaitForNextUpdateState(false);
    try {
      const result = await runGeneration({
        brandId: activeBrandId,
        loadCount: async () => {
          const data = await fetchOptimizationsData();
          const { activeGroups: agCount, expiredGroups: egCount } = normalizeSuggestionsResponse(data);
          return agCount.length + egCount.length;
        },
      });
      if (!result.ok) return;
      const hasAny = result.count > 0;
      if (hasAny) {
        toast.success('Sugerencias generadas correctamente');
      } else {
        setMustWaitForNextUpdate(activeBrandId);
        setMustWaitForNextUpdateState(true);
      }
      setOptimizationsRefreshToken((t) => t + 1);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Error al generar sugerencias';
      toast.error(errorMessage);
    }
  }, [activeBrandId, runGeneration, fetchOptimizationsData]);

  // Cargar optimizaciones desde la API
  const loadOptimizations = useCallback(async () => {
    if (!activeBrandId) {
      setActiveGroups([]);
      setExpiredGroups([]);
      setLoading(false);
      return;
    }

    // Esperar a que los objetivos terminen de cargar antes de cargar optimizaciones
    if (loadingObjectives) {
      return;
    }

    setLoading(true);
    try {
      const data = await fetchOptimizationsData();
      const { activeGroups: ag, expiredGroups: eg } = normalizeSuggestionsResponse(data);
      setActiveGroups(ag);
      setExpiredGroups(eg);
    } catch (error) {
      console.error('Error cargando optimizaciones:', error);
      setActiveGroups([]);
      setExpiredGroups([]);
    } finally {
      setLoading(false);
    }
  }, [activeBrandId, loadingObjectives, fetchOptimizationsData]);

  useEffect(() => {
    if (!activeBrandId) {
      setActiveGroups([]);
      setExpiredGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
  }, [activeBrandId]);

  // Cargar optimizaciones después de que los objetivos estén cargados
  useEffect(() => {
    if (!activeBrandId || loadingObjectives) {
      return;
    }
    loadOptimizations();
  }, [activeBrandId, loadingObjectives, objectives, loadOptimizations, optimizationsRefreshToken]);

  const filterSuggestionByPlatform = useCallback(
    (s) => {
      if (filtroPlataforma === 'todas') return true;
      const platformLower = (s.platform || '').toLowerCase();
      if (filtroPlataforma === 'google') return platformLower.includes('google');
      if (filtroPlataforma === 'meta') return platformLower.includes('meta');
      return true;
    },
    [filtroPlataforma]
  );

  const filteredActiveGroups = useMemo(
    () =>
      activeGroups
        .map((g) => ({
          ...g,
          suggestions: (g.suggestions || []).filter(filterSuggestionByPlatform),
        }))
        .filter((g) => g.suggestions.length > 0),
    [activeGroups, filterSuggestionByPlatform]
  );
  const filteredExpiredGroups = useMemo(
    () =>
      expiredGroups
        .map((g) => ({
          ...g,
          suggestions: (g.suggestions || []).filter(filterSuggestionByPlatform),
        }))
        .filter((g) => g.suggestions.length > 0),
    [expiredGroups, filterSuggestionByPlatform]
  );

  /** Una tarjeta Kanban por grupo de API (regla + sugerencias), con merge de reglas especiales. */
  const kanbanGroupCards = useMemo(
    () =>
      buildKanbanGroupCardsFromFilteredGroups(
        filteredActiveGroups,
        filteredExpiredGroups,
        getObjectiveName
      ),
    [filteredActiveGroups, filteredExpiredGroups, getObjectiveName]
  );

  const kanbanBuckets = useMemo(
    () => distributeKanbanGroupCardsToBuckets(kanbanGroupCards),
    [kanbanGroupCards]
  );

  const allFlatSuggestionCards = useMemo(
    () => flattenKanbanCardsForModalNavigation(kanbanGroupCards),
    [kanbanGroupCards]
  );

  const optimizationsForDetailModal = detailModalOptimizations ?? allFlatSuggestionCards;

  /** Misma fusión que el Kanban (reglas especiales + dedupe), para que el carrusel del modal coincida 1:1 con las tarjetas. */
  const allGroupsForDetailModal = useMemo(() => {
    const mergedActive = mergeApiGroupsByLogicalDedupeKey(
      mergeSpecialOptimizationGroups(Array.isArray(filteredActiveGroups) ? filteredActiveGroups : [])
    );
    const mergedExpired = mergeApiGroupsByLogicalDedupeKey(
      mergeSpecialOptimizationGroups(Array.isArray(filteredExpiredGroups) ? filteredExpiredGroups : [])
    );
    return [...mergedActive, ...mergedExpired].map((g) => ({
      ruleKey: g.rule?.key ?? null,
      cards: (g.suggestions || []).map((s) => suggestionToCard(s, getObjectiveName)),
    }));
  }, [filteredActiveGroups, filteredExpiredGroups, getObjectiveName]);

  const handleViewDetails = useCallback(
    (card) => {
      const columnKey = Object.keys(kanbanBuckets).find((key) =>
        (kanbanBuckets[key] || []).some((c) => c.id === card.id)
      );
      const columnGroupCards = columnKey ? kanbanBuckets[columnKey] || [] : [];
      const columnFlat = flattenKanbanCardsForModalNavigation(columnGroupCards);
      const representative =
        Array.isArray(card.childCards) && card.childCards.length > 0 ? card.childCards[0] : card;
      setDetailModalOptimizations(columnFlat.length > 0 ? columnFlat : allFlatSuggestionCards);
      const groupIdx = kanbanGroupCards.findIndex((c) => c.id === card.id);
      setCurrentGroupIndex(groupIdx >= 0 ? groupIdx : 0);
      const index = (columnFlat.length > 0 ? columnFlat : allFlatSuggestionCards).findIndex(
        (c) => c.id === representative.id
      );
      setSelectedOptimization(representative);
      setCurrentOptimizationIndex(index >= 0 ? index : 0);
      setGlobalOptimizationDetailModalOpen(true);
      setIsModalOpen(true);
    },
    [
      kanbanBuckets,
      kanbanGroupCards,
      allFlatSuggestionCards,
      setGlobalOptimizationDetailModalOpen,
    ]
  );

  const handleNavigateGroup = useCallback((newGroupIndex) => {
    if (newGroupIndex < 0 || newGroupIndex >= allGroupsForDetailModal.length) return;
    setCurrentGroupIndex(newGroupIndex);
    const groupCards = allGroupsForDetailModal[newGroupIndex]?.cards;
    if (groupCards && groupCards[0]) {
      setSelectedOptimization(groupCards[0]);
      setCurrentOptimizationIndex(0);
    }
  }, [allGroupsForDetailModal]);

  const handleNavigateOptimization = useCallback((newIndex) => {
    if (newIndex >= 0 && newIndex < optimizationsForDetailModal.length) {
      setSelectedOptimization(optimizationsForDetailModal[newIndex]);
      setCurrentOptimizationIndex(newIndex);
    }
  }, [optimizationsForDetailModal]);

  const [animatingCardIds, setAnimatingCardIds] = useState({});

  const handleRatingSuccess = useCallback((optimizationId, is_liked, updates = {}) => {
    const applyPatch = (groups) =>
      groups.map((g) => ({
        ...g,
        suggestions: (g.suggestions || []).map((s) =>
          s.id === optimizationId ? { ...s, is_liked, ...updates } : s
        ),
      }));

    if (updates.status === 'DISMISSED') {
      setAnimatingCardIds((prev) => ({ ...prev, [optimizationId]: 'DISMISSED' }));
      setTimeout(() => {
        setActiveGroups((prev) => applyPatch(prev));
        setExpiredGroups((prev) => applyPatch(prev));
        setAnimatingCardIds((prev) => {
          const next = { ...prev };
          delete next[optimizationId];
          return next;
        });
        setSelectedOptimization((prev) =>
          prev?.id === optimizationId ? { ...prev, is_liked, ...updates } : prev
        );
      }, 600);
      return;
    }

    setActiveGroups((prev) => applyPatch(prev));
    setExpiredGroups((prev) => applyPatch(prev));
    setSelectedOptimization((prev) =>
      prev?.id === optimizationId ? { ...prev, is_liked, ...updates } : prev
    );
  }, []);

  const handleApplySuccess = useCallback((suggestionId, newStatus) => {
    // Mark the card as animating with its new status
    setAnimatingCardIds((prev) => ({ ...prev, [suggestionId]: newStatus }));

    // After animation delay, update the actual data so the card moves columns
    setTimeout(() => {
      const updateSuggestions = (groups) =>
        groups.map((g) => ({
          ...g,
          suggestions: (g.suggestions || []).map((s) =>
            s.id === suggestionId
              ? {
                  ...s,
                  status: newStatus,
                  ...(String(newStatus || '').toUpperCase() === 'APPLIED'
                    ? { applied_at: s.applied_at ?? new Date().toISOString() }
                    : {}),
                }
              : s
          ),
        }));
      setActiveGroups((prev) => updateSuggestions(prev));
      setExpiredGroups((prev) => updateSuggestions(prev));
      setAnimatingCardIds((prev) => {
        const next = { ...prev };
        delete next[suggestionId];
        return next;
      });
    }, 600);
  }, []);

  const totalUnfilteredSuggestionsCount = useMemo(
    () =>
      groupsToSuggestions(activeGroups).length + groupsToSuggestions(expiredGroups).length,
    [activeGroups, expiredGroups]
  );

  const handleTestGenerateSuggestionsRef = useRef(handleTestGenerateSuggestions);
  handleTestGenerateSuggestionsRef.current = handleTestGenerateSuggestions;
  const autoPageGenFiredRef = useRef({});

  useEffect(() => {
    autoPageGenFiredRef.current = {};
  }, [activeBrandId]);

  useEffect(() => {
    if (!activeBrandId || !hasAnyConnection || loading || loadingObjectives || objectives.length === 0) return;
    if (totalUnfilteredSuggestionsCount > 0) return;
    if (hasTriedGeneration) return;
    if (mustWaitForNextUpdate) return;
    if (isGenerating && generationState.brandId === activeBrandId) return;
    const key = String(activeBrandId);
    if (autoPageGenFiredRef.current[key]) return;
    autoPageGenFiredRef.current[key] = true;
    void handleTestGenerateSuggestionsRef.current();
  }, [
    activeBrandId,
    hasAnyConnection,
    loading,
    loadingObjectives,
    objectives.length,
    totalUnfilteredSuggestionsCount,
    hasTriedGeneration,
    mustWaitForNextUpdate,
    isGenerating,
    generationState.brandId,
  ]);

  // Estado vacío si no hay cuentas conectadas
  if (!hasAnyConnection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-[5rem] px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          <NoAdsConnectedSection
            connectGoogleAds={connectGoogleAds}
            startMetaOAuth={startMetaOAuth}
            activePlatform={'all'}
            activeGoogleAccount={null}
            activeMetaAccount={null}
            context={'optimizaciones'}
          />
        </div>
      </div>
    );
  }

  // Bloqueado: tiene cuenta conectada pero no tiene objetivos en la marca activa
  if (
    hasAnyConnection &&
    !loadingObjectives &&
    Array.isArray(objectives) &&
    objectives.length === 0
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-[5rem] px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          <NoObjectivesLockedSection
            onCreateClick={() => navigate('/objetivos')}
            platformsWithoutObjectives={platformsWithoutObjectives}
          />
        </div>
      </div>
    );
  }

  const connectPlatformPrompt =
    filtroPlataforma === 'meta' && !isMetaConnected
      ? { platform: 'meta', label: 'Meta Ads', onConnect: startMetaOAuth }
      : filtroPlataforma === 'google' && !isGoogleConnected
      ? { platform: 'google', label: 'Google Ads', onConnect: connectGoogleAds }
      : null;
  const shouldShowCountdownBanner =
    loading ||
    loadingObjectives ||
    Boolean(connectPlatformPrompt);

  const pageIsGeneratingOptimizations =
    isGenerating && generationState.brandId === activeBrandId;
  const hasGenerationErrorForBrand =
    generationState.status === 'error' &&
    String(generationState.brandId ?? '') === String(activeBrandId ?? '');
  const generationErrorMessage = hasGenerationErrorForBrand
    ? generationState.error || 'No pudimos generar optimizaciones.'
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-20 px-6 pb-6">
      <div className="max-w-7xl mx-auto">
        <OptimizacionesFilterBar
          filtroPlataforma={filtroPlataforma}
          setFiltroPlataforma={setFiltroPlataforma}
          filtroEstatus={filtroEstatus}
          setFiltroEstatus={setFiltroEstatus}
        />
        {shouldShowCountdownBanner && (
          <OptimizationsCountdownBanner
            hasOptimizations={totalUnfilteredSuggestionsCount > 0}
            isGenerating={pageIsGeneratingOptimizations}
            mustWaitForNextUpdate={mustWaitForNextUpdate}
            isLoading={loading || loadingObjectives}
            connectPlatformPrompt={connectPlatformPrompt}
          />
        )}

        {/* Listado de optimizaciones - tarjetas por grupo */}
        <div className="w-full px-2 sm:px-4 mb-8">
          <div className="relative z-10">
            {(loading || loadingObjectives) ? (
              <div
                className="flex flex-col items-center justify-center py-12 px-4 text-white/70"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <p className="text-sm font-medium text-white/80 mb-5">
                  {loadingObjectives ? 'Cargando objetivos…' : 'Cargando optimizaciones…'}
                </p>
                <div className="w-full max-w-md space-y-3" aria-hidden={true}>
                  <div className="h-3 w-full rounded-lg bg-white/10 animate-pulse motion-reduce:animate-none" />
                  <div className="h-3 w-[88%] mx-auto rounded-lg bg-white/10 animate-pulse motion-reduce:animate-none" />
                  <div className="h-3 w-full rounded-lg bg-white/10 animate-pulse motion-reduce:animate-none" />
                </div>
              </div>
            ) : kanbanGroupCards.length === 0 ? (
              <div className="rounded-2xl border border-white/20 bg-gradient-to-br from-slate-950/55 via-slate-900/55 to-indigo-900/55 p-4 sm:p-5 shadow-[0_18px_40px_-24px_rgba(99,102,241,0.45)]">
                <div className="mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-fuchsia-300" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white/90">Optimizaciones</h3>
                </div>
                <div className="flex min-h-[240px] items-center justify-center rounded-xl border border-white/10 border-dashed bg-gradient-to-br from-slate-900/45 via-slate-800/35 to-indigo-900/40 px-4 py-8 text-center text-white/70">
                  {totalUnfilteredSuggestionsCount > 0 ? (
                    <p className="max-w-md px-2 text-sm text-white/85 leading-relaxed">
                      No se encontraron optimizaciones con los filtros seleccionados. Probá cambiar plataforma o estado, o limpiar filtros.
                    </p>
                  ) : mustWaitForNextUpdate ? (
                    <div className="w-full flex items-center justify-center">
                      <OptimizationsCountdownBanner
                        hasOptimizations={false}
                        isGenerating={pageIsGeneratingOptimizations}
                        mustWaitForNextUpdate={mustWaitForNextUpdate}
                        isLoading={false}
                      />
                    </div>
                  ) : hasGenerationErrorForBrand ? (
                    <div
                      role="alert"
                      className="mx-auto w-full max-w-md rounded-xl border border-red-400/35 bg-gradient-to-r from-red-500/20 to-rose-500/20 px-4 py-6 text-center shadow-lg space-y-3"
                    >
                      <p className="text-sm text-red-100 leading-relaxed">
                        {generationErrorMessage}
                      </p>
                      <button
                        type="button"
                        onClick={handleTestGenerateSuggestions}
                        disabled={pageIsGeneratingOptimizations}
                        className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-gradient-to-r from-fuchsia-600 to-blue-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                      >
                        {pageIsGeneratingOptimizations ? 'Reintentando…' : 'Reintentar'}
                      </button>
                    </div>
                  ) : hasTriedGeneration && !pageIsGeneratingOptimizations ? (
                    <div className="mx-auto w-full max-w-md rounded-xl border border-fuchsia-300/25 bg-gradient-to-r from-fuchsia-500/20 to-indigo-500/20 px-4 py-6 text-center shadow-lg">
                      <p className="text-sm font-medium text-fuchsia-100 leading-relaxed">
                        Ya intentamos generar optimizaciones para esta marca. Ahora estamos esperando la próxima actualización automática.
                      </p>
                    </div>
                  ) : (
                    <div
                      className="mx-auto w-full max-w-md space-y-3 rounded-xl border border-fuchsia-400/20 bg-white/5 px-4 py-6 text-left"
                      role="status"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <div className="flex items-center justify-center gap-2 text-fuchsia-200">
                        <RefreshCw
                          className="h-5 w-5 shrink-0 animate-spin motion-reduce:animate-none"
                          aria-hidden={true}
                        />
                        <span className="text-sm font-medium text-center">
                          {pageIsGeneratingOptimizations
                            ? 'Generando optimizaciones con IA…'
                            : 'Iniciando generación automática…'}
                        </span>
                      </div>
                      <p className="text-center text-xs text-white/55 leading-relaxed px-1">
                        El avance también aparece en la notificación inferior derecha. Podés seguir navegando mientras tanto.
                      </p>
                      <div className="space-y-2" aria-hidden={true}>
                        <div className="h-2.5 w-full animate-pulse motion-reduce:animate-none rounded-lg bg-white/10" />
                        <div className="mx-auto h-2.5 w-[80%] animate-pulse motion-reduce:animate-none rounded-lg bg-white/10" />
                        <div className="h-2.5 w-full animate-pulse motion-reduce:animate-none rounded-lg bg-white/10" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-end">
                  <div
                    role="group"
                    aria-label="Vista de optimizaciones"
                    className="inline-flex rounded-xl border border-white/15 bg-white/5 p-0.5 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() => setViewMode('board')}
                      aria-pressed={viewMode === 'board'}
                      aria-label="Vista en columnas"
                      title="Vista en columnas"
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                        viewMode === 'board'
                          ? 'bg-white/15 text-white shadow-sm'
                          : 'text-white/50 hover:bg-white/10 hover:text-white/80'
                      }`}
                    >
                      <LayoutGrid className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      aria-pressed={viewMode === 'list'}
                      aria-label="Vista en lista"
                      title="Vista en lista"
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                        viewMode === 'list'
                          ? 'bg-white/15 text-white shadow-sm'
                          : 'text-white/50 hover:bg-white/10 hover:text-white/80'
                      }`}
                    >
                      <List className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </div>
                <KanbanBoard
                  buckets={kanbanBuckets}
                  onCardClick={handleViewDetails}
                  onRatingSuccess={handleRatingSuccess}
                  isMobile={isMobileOrTablet}
                  animatingCardIds={animatingCardIds}
                  viewMode={viewMode}
                  visibleColumnKeys={visibleKanbanColumnKeys}
                />
              </>
            )}
          </div>
        </div>

        {/* Píldoras turquesas: plataformas sin optimizaciones (no conectada o sin objetivos) */}
        {!loading && !loadingObjectives && platformsWithNoOptimizations.length > 0 && (
          <div className="w-full px-2 sm:px-4 mt-6 mb-8 flex flex-wrap items-center justify-center gap-3">
            {platformsWithNoOptimizations.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => {
                  if (p.reason === 'connect') {
                    p.value === 'GOOGLE' ? connectGoogleAds(false) : startMetaOAuth(false);
                  } else {
                    openCreateObjectivesSetupModal({ platform: p.value });
                  }
                }}
                className={
                  p.reason === 'connect'
                    ? 'inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-[0.98]'
                    : 'inline-flex items-center gap-3 backdrop-blur-xl bg-gradient-to-r from-cyan-500/20 via-teal-500/20 to-cyan-600/20 border border-cyan-400/30 rounded-full px-5 py-2.5 shadow-[0_4px_14px_-2px_rgba(6,182,212,0.3),0_4px_20px_-4px_rgba(20,184,166,0.4)] hover:border-cyan-400/50 transition-all duration-300'
                }
              >
                {p.reason === 'connect' && p.value === 'GOOGLE' && <GoogleIcon width={24} height={24} />}
                {p.reason === 'connect' && p.value === 'META' && <MetaIcon width={24} height={24} />}
                <span className={p.reason === 'connect' ? 'text-white text-base font-semibold whitespace-nowrap' : 'text-white/90 text-sm font-medium whitespace-nowrap'}>
                  {p.reason === 'connect'
                    ? `Conectá ${p.label}`
                    : `Creá objetivos de ${p.label} para generar optimizaciones`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal de detalle: admins ven la tarjeta v3 (SuggestionCard) solo en Meta; Google y los no-admin ven la legacy. */}
      {(() => {
        // Google se queda con la tarjeta legacy (la que ven los no-admin en producción);
        // el rediseño v3 aplica solo a Meta.
        const isGoogleSelected = isGoogleAdsPlatformUi(selectedOptimization?.platform ?? selectedOptimization?._raw?.platform);
        const DetailModalComponent = (isAdmin && !isGoogleSelected) ? OptimizationDetailModalV3 : OptimizationDetailModal;
        return (
          <DetailModalComponent
            isOpen={isModalOpen}
            onClose={() => {
              setGlobalOptimizationDetailModalOpen(false);
              setIsModalOpen(false);
              setSelectedOptimization(null);
              setCurrentOptimizationIndex(0);
              setDetailModalOptimizations(null);
              setCurrentGroupIndex(0);
            }}
            optimization={selectedOptimization}
            allOptimizations={optimizationsForDetailModal}
            currentIndex={currentOptimizationIndex}
            onNavigate={handleNavigateOptimization}
            onRatingSuccess={handleRatingSuccess}
            onApplySuccess={handleApplySuccess}
            allGroups={allGroupsForDetailModal}
            currentGroupIndex={currentGroupIndex}
            onNavigateGroup={handleNavigateGroup}
          />
        );
      })()}
    </div>
  );
};

export default Optimizaciones;
